import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import D3EDisposable from "../rocket/D3EDisposable";
import Notification from "../models/Notification";
import Administrator from "../models/Administrator";
import MaterialIcons from "../icons/MaterialIcons";
import PageNavigator from "../classes/PageNavigator";
import SearchRequest from "../models/SearchRequest";
import RequestResponseView from "./RequestResponseView";
import IconButton from "./IconButton";
import Result from "../classes/Result";
import MessageDispatch from "../rocket/MessageDispatch";
import Query from "../classes/Query";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface SearchRequestResponseViewProps extends BaseUIProps {
  key?: string;
  obj: SearchRequest;
  admin?: Administrator;
}

class _SearchRequestResponseViewState extends ObservableComponent<SearchRequestResponseViewProps> {
  static defaultProps = { obj: null, admin: null };
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public objDisposable: D3EDisposable;
  public constructor(props: SearchRequestResponseViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): SearchRequest {
    return this.props.obj;
  }
  public get admin(): Administrator {
    return this.props.admin;
  }
  public initState() {
    super.initState();

    this.runFetchDataQueryForObj();

    this.objDisposable = MessageDispatch.get().syncObject(
      this.props.obj,
      UsageConstants.QUERY_GETSEARCHREQUESTBYID_SEARCHREQUESTRESPONSEVIEW_OBJ_FETCHDATA
    );

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.updateSyncProperty("admin", this.props.admin);

    this.on(["obj"], this.rebuild);
  }
  public componentDidUpdate(prevProps: SearchRequestResponseViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.runFetchDataQueryForObj();

      this.fire("obj", this);
    }

    if (prevProps.admin !== this.props.admin) {
      this.updateObservable("admin", prevProps.admin, this.props.admin);

      this.fire("admin", this);
    }
  }
  public runFetchDataQueryForObj = (): void => {
    if (this.obj == null) {
      return;
    }

    Query.get().getSearchRequestById(
      UsageConstants.QUERY_GETSEARCHREQUESTBYID_SEARCHREQUESTRESPONSEVIEW_OBJ_FETCHDATA,
      this.obj.id
    );
  };
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Row({
          children: [
            IconButton({
              size: 25,
              icon: MaterialIcons.arrow_back,
              tooltip: "Back",
              hoverColor: cStyle.c19,
              onPressed: () => {
                this.backButtonHandler();
              },
              key: "0",
            }),
          ],
          className: "x3fc0 hc h",
          key: "0",
        }),
        ui.Container({
          margin: ui.EdgeInsets.fromLTRB(0.0, 10.0, 0.0, 0.0, new Map()),
          child: RequestResponseView({ obj: this.obj }),
          key: "1",
          className: "x487 hc h",
        }),
      ],
      className: ui.join(
        this.props.className,
        "SearchRequestResponseView xdf hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = async (): Promise<void> => {
    if (
      this.obj.notification !== null &&
      this.obj.notification.viewed === false
    ) {
      this.obj.notification.setViewed(true);

      let result: Result<Notification> = await this.obj.notification.save();
    }
  };
  public backButtonHandler = (): void => {
    if (this.admin !== null) {
      this.navigator.pushSystemAdminRequestList({
        user: null,
        admin: this.admin,
        target: "main",
        replace: false,
      });
    } else {
      this.navigator.pushStaffRequestTab({
        user: this.obj.createdBy,
        target: "main",
        replace: false,
      });
    }
  };
  public dispose(): void {
    this.objDisposable?.dispose();

    super.dispose();
  }
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
}
export default function SearchRequestResponseView(
  props: SearchRequestResponseViewProps
) {
  return React.createElement(_SearchRequestResponseViewState, {
    ..._SearchRequestResponseViewState.defaultProps,
    ...props,
  });
}
